import { useEffect, useState } from 'react';
import { api, type MyTools } from '../api/client';
import { useOptionalAuth } from '../context/AuthContext';

/* Which Tools this login may open (ADR 0031): GET /auth/me/tools, asked once per login
   (the sidebar mounts on every page). The launch stage is the operator's; the per-Account
   flags are Team's. A failed check is not remembered. */
let cached: { userId: string; tools: Promise<MyTools>; value?: MyTools } | null = null;

function myTools(userId: string): Promise<MyTools> {
  if (cached?.userId !== userId) {
    const entry: { userId: string; tools: Promise<MyTools>; value?: MyTools } = {
      userId,
      tools: api.getMyTools().then((res) => {
        entry.value = res;
        return res;
      }),
    };
    entry.tools.catch(() => {
      if (cached === entry) cached = null;
    });
    cached = entry;
  }
  return cached.tools;
}

/** Nothing launched, nothing usable: what a failed check or no login falls back to. */
export const NO_TOOLS: MyTools = {
  launched: { bulk_media: false, audio: false, dynamic_lists: false, tracking_links: false },
  usable: { copy: false, bulk_media: false, audio: false, dynamic_lists: false, tracking_links: false },
  mass_pages: { mass_messages: [], priority_mass_messages: [], automated_messages: [], chat_assistant: [] },
  tool_accounts: { dynamic_lists: [], tracking_links: [] },
};

/** The answer once known, null while in flight. Outside an AuthProvider: NO_TOOLS. */
export function useToolAccess(): MyTools | null {
  const auth = useOptionalAuth();
  const userId = auth?.user?.id ?? '';
  const signedIn = Boolean(auth?.isAuthenticated && userId);
  const known = !auth ? NO_TOOLS : cached?.userId === userId ? cached.value ?? null : null;
  const [tools, setTools] = useState<MyTools | null>(known);

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    myTools(userId)
      .then((res) => {
        if (live) setTools(res);
      })
      .catch(() => {
        // Hide the Tools rather than open pages that would 403.
        if (live) setTools(NO_TOOLS);
      });
    return () => {
      live = false;
    };
  }, [signedIn, userId]);

  return tools;
}
